// frontend/src/utils/suggestionMode.js

// Attribute keys for tracked changes
const ATTR_INSERT = "suggest-insert";
const ATTR_DELETE = "suggest-delete";

export function getSuggestionRangeAtCursor(quill) {
  if (!quill) return null;

  const sel = quill.getSelection();

  if (!sel || typeof sel.index !== "number") return null;

  const len = quill.getLength();
  const probe = (i) => (i >= 0 && i < len ? quill.getFormat(i, 1) : {}); let seed = sel.index;

  let fmt = probe(seed);
  let kind = fmt[ATTR_INSERT] ? "insert" : (fmt[ATTR_DELETE] ? "delete" : null);

  if (!kind && seed > 0) {
    seed -= 1;
    fmt = probe(seed);
    kind = fmt[ATTR_INSERT] ? "insert" : (fmt[ATTR_DELETE] ? "delete" : null);
  }
  if (!kind) return null;

  let start = seed;
  while (start > 0) {
    const f = probe(start - 1);
    if ((kind === "insert" && f[ATTR_INSERT]) || (kind === "delete" && f[ATTR_DELETE])) {
      start -= 1;
    } else break; 
  }

  let end = seed + 1;
  while (end < len) { 
    const f = probe(end);
    if ((kind === "insert" && f[ATTR_INSERT]) || (kind === "delete" && f[ATTR_DELETE])) { 
      end += 1;
    } else break;
  }

  const result = { kind, index: start, length: end - start };
  return result;
}

export function getSuggestionRangeAtIndex(quill, index) {
  if (!quill || typeof index !== "number") return null;

  const len = quill.getLength();
  const clamp = (i) => Math.max(0, Math.min(len - 1, i));
  const probe = (i) => (i >= 0 && i < len ? quill.getFormat(i, 1) : {});

  let seed = clamp(index);
  let fmt = probe(seed);
  let kind = fmt[ATTR_INSERT] ? "insert" : (fmt[ATTR_DELETE] ? "delete" : null);

  if (!kind && seed > 0) {
    seed -= 1;
    fmt = probe(seed);
    kind = fmt[ATTR_INSERT] ? "insert" : (fmt[ATTR_DELETE] ? "delete" : null);
  }
  if (!kind) return null;

  let start = seed;
  while (start > 0) {
    const f = probe(start - 1);
    if ((kind === "insert" && f[ATTR_INSERT]) || (kind === "delete" && f[ATTR_DELETE])) {
      start -= 1;
    } else break;
  }

  let end = seed + 1;
  while (end < len) {
    const f = probe(end);
    if ((kind === "insert" && f[ATTR_INSERT]) || (kind === "delete" && f[ATTR_DELETE])) {
      end += 1;
    } else break;
  }

  const result = { kind, index: start, length: end - start };
  return result;
}

export function getActiveSuggestionRange(quill, fallbackIndex) {
  return (
    getSuggestionRangeAtCursor(quill) ||
    getSuggestionRangeAtIndex(
      quill,
      typeof fallbackIndex === "number"
        ? fallbackIndex
        : (quill.getSelection()?.index ?? 0)
    )
  );
}

export function acceptSuggestionRange(quill, r, fallbackIndex) {
  if (!quill) return false;
  r = r || getActiveSuggestionRange(quill, fallbackIndex);
  if (!r) return false;

  if (r.kind === "insert") {
    quill.formatText(r.index, r.length, ATTR_INSERT, false);
  } else {
    quill.deleteText(r.index, r.length);
  }
  return true;
}

export function rejectSuggestionRange(quill, r, fallbackIndex) {
  if (!quill) return false;
  r = r || getActiveSuggestionRange(quill, fallbackIndex);
  if (!r) return false;

  if (r.kind === "insert") {
    quill.deleteText(r.index, r.length);
  } else {
    quill.formatText(r.index, r.length, ATTR_DELETE, false);
  }
  return true;
}

export function acceptAllSuggestions(quill) {
  if (!quill) return;
  const ops = quill.getContents().ops || [];
  let i = 0, ranges = [];
  ops.forEach(op => {
    const len = typeof op.insert === "string" ? op.insert.length : (op.insert ? 1 : 0);
    if (len > 0 && op.attributes?.[ATTR_INSERT]) ranges.push({ kind: "insert", i, len });
    if (len > 0 && op.attributes?.[ATTR_DELETE]) ranges.push({ kind: "delete", i, len });
    i += len;
  });
  ranges.sort((a, b) => b.i - a.i).forEach(r => {
    if (r.kind === "insert") {
      quill.formatText(r.i, r.len, ATTR_INSERT, false, "silent");
    } else {
      quill.deleteText(r.i, r.len, "silent");
    }
  });
}

export function rejectAllSuggestions(quill) {
  if (!quill) return;
  const ops = quill.getContents().ops || [];
  let i = 0, ranges = [];
  ops.forEach(op => {
    const len = typeof op.insert === "string" ? op.insert.length : (op.insert ? 1 : 0);
    if (len > 0 && op.attributes?.[ATTR_INSERT]) ranges.push({ kind: "insert", i, len });
    if (len > 0 && op.attributes?.[ATTR_DELETE]) ranges.push({ kind: "delete", i, len });
    i += len;
  });
  ranges.sort((a, b) => b.i - a.i).forEach(r => {
    if (r.kind === "insert") {
      quill.deleteText(r.i, r.len, "silent");
    } else {
      quill.formatText(r.i, r.len, ATTR_DELETE, false, "silent");
    }
  });
}
